import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ButtonPrimary } from '@components/button/button-primary';
import { ButtonSecondary } from '@components/button/button-secondary';
import { IconEmpty } from '@components/icons/icon-empty';
import { IconOfflineSale } from '@components/icons/icon-offline-sale';
import { ModalFooter } from '@components/modals/modal-footer';
import { ModalHeader } from '@components/modals/modal-header';
import { OFFER_TAB_PARAM, TAB_PASS } from '@components/offers/offers-content/offers-content.type';
import { ENABLED_CREATE_COMPANY_OFFERS_BUTTON, ENABLED_CREATE_EVENT_BUTTON } from '@constants/company-auth-permissions';
import { EVENT_CREATE_INFO_ROUTE, PRESET_LIST_ROUTE } from '@constants/routes';
import { CompanyClubContext } from '@contexts/company-club/company-club-context';
import { useEventCreationRevoked } from '@hooks/use-event-creation-revoked/use-event-creation-revoked';
import { useIsProtectedElement } from '@hooks/use-is-protected-element';

import type { EmptyStateProps } from './empty-state.type';

export const EmptyState = ({ onClose }: EmptyStateProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyState, clubState } = useContext(CompanyClubContext);
  const companyLegacyId = companyState.legacyId;
  const clubSlug = clubState.slug;

  const hasCreateEventPermission = useIsProtectedElement({ permissions: ENABLED_CREATE_EVENT_BUTTON });
  const { isEventCreationRevoked } = useEventCreationRevoked();
  const canCreateEvent = hasCreateEventPermission && !isEventCreationRevoked;
  const canCreatePass = useIsProtectedElement({ permissions: ENABLED_CREATE_COMPANY_OFFERS_BUTTON });
  const canCreateAny = canCreateEvent || canCreatePass;
  // Guard the navigation handlers against a not-yet-hydrated CompanyClubContext —
  // both fields default to '' there and would produce e.g. `//events/info`.
  const hasVenueContext = !!companyLegacyId && !!clubSlug;

  const description = canCreateAny
    ? t('offers:offlineSalesEmptyStateAdminDescription')
    : t('offers:offlineSalesEmptyStateRestrictedDescription');

  const handleCreateEvent = () => {
    if (!hasVenueContext) return;
    onClose();
    navigate(EVENT_CREATE_INFO_ROUTE.replace(':companyId', companyLegacyId).replace(':clubSlug', clubSlug));
  };

  const handleCreatePass = () => {
    if (!hasVenueContext) return;
    onClose();
    const baseUrl = PRESET_LIST_ROUTE.replace(':companyId', companyLegacyId).replace(':clubSlug', clubSlug);
    navigate(`${baseUrl}?${OFFER_TAB_PARAM}=${TAB_PASS}`);
  };

  return (
    <>
      <ModalHeader title={t('offers:offlineSalesTitle')} primaryIcon={IconOfflineSale} />
      <div className='flex flex-col items-center justify-center gap-4 px-4 py-12 sm:px-20 sm:py-16'>
        <div className='flex items-center justify-center w-16 h-16 rounded-full bg-secondary'>
          <IconEmpty className='w-8 h-8 text-tertiary' />
        </div>
        <h3 className='text-headline-s sm:text-headline-m text-primary text-center'>
          {t('offers:offlineSalesEmptyStateTitle')}
        </h3>
        <p className='text-body-m text-secondary text-center max-w-md'>{description}</p>
        {canCreateAny && (
          <div className='flex flex-col sm:flex-row gap-2 mt-4 w-full sm:w-auto'>
            {canCreateEvent && (
              <ButtonPrimary onClick={handleCreateEvent} disabled={!hasVenueContext} className='flex-1 sm:flex-none'>
                {t('offers:offlineSalesEmptyStateCreateEventButton')}
              </ButtonPrimary>
            )}
            {canCreatePass && (
              <ButtonSecondary onClick={handleCreatePass} disabled={!hasVenueContext} className='flex-1 sm:flex-none'>
                {t('offers:offlineSalesEmptyStateCreatePassButton')}
              </ButtonSecondary>
            )}
          </div>
        )}
      </div>
      <ModalFooter secondaryButtonText={t('common:closeLabel')} onSecondaryClick={onClose} />
    </>
  );
};
